import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { 
  Radio, 
  Bell, 
  TrendingUp, 
  Zap,
  ArrowRight,
  ExternalLink,
  Clock
} from 'lucide-react';
import { FaTelegram } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentAlerts, setRecentAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, alertsRes] = await Promise.all([
          axios.get(`${API}/dashboard/stats`, { withCredentials: true }),
          axios.get(`${API}/dashboard/recent-alerts`, { withCredentials: true })
        ]);
        setStats(statsRes.data);
        setRecentAlerts(alertsRes.data.alerts || []);
      } catch (error) {
        toast.error('Failed to load dashboard data');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const usagePercent = stats ? Math.round((stats.usage_this_month / stats.usage_limit) * 100) : 0;

  const statCards = [
    {
      title: 'Active Monitors',
      value: stats?.active_monitors || 0,
      icon: Radio,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      link: '/monitors'
    },
    {
      title: 'Alerts This Week',
      value: stats?.alerts_this_week || 0,
      icon: Bell,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      link: '/alerts'
    },
    {
      title: 'Total Alerts',
      value: stats?.total_alerts || 0,
      icon: TrendingUp,
      color: 'text-success',
      bgColor: 'bg-success/10',
      link: '/alerts'
    }
  ];

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Welcome Header */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        >
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold font-['Outfit'] tracking-tight" data-testid="dashboard-welcome">
              Welcome back, {user?.name?.split(' ')[0]}
            </h1>
            <p className="text-muted-foreground mt-1">
              Here&apos;s what&apos;s happening with your monitors
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant={stats?.plan === 'free' ? 'secondary' : 'default'} className="px-3 py-1">
              {stats?.plan?.charAt(0).toUpperCase() + stats?.plan?.slice(1) || 'Free'} Plan
            </Badge>
            {stats?.plan === 'free' && (
              <Button asChild size="sm" className="rounded-full gap-2">
                <Link to="/billing">
                  <Zap className="w-4 h-4" />
                  Upgrade
                </Link>
              </Button>
            )}
          </div>
        </motion.div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {statCards.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <motion.div
                key={stat.title}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={stat.link}>
                  <Card className="card-hover border-border/50">
                    <CardContent className="p-6">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">{stat.title}</p>
                          <p className="text-3xl font-bold font-['Outfit'] mt-1">{stat.value}</p>
                        </div>
                        <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 ${stat.color}`} />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Usage & Quick Actions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Usage Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Monthly Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">Alerts used</span>
                    <span className="font-medium">
                      {stats?.usage_this_month || 0} / {stats?.usage_limit || 50}
                    </span>
                  </div>
                  <Progress value={usagePercent} className="h-2" />
                  {usagePercent >= 80 && (
                    <p className="text-sm text-accent">
                      You&apos;re approaching your limit. Consider upgrading for more alerts.
                    </p>
                  )}
                  <Button asChild variant="outline" className="w-full rounded-full">
                    <Link to="/billing" className="gap-2">
                      View Plans
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          {/* Quick Actions */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <Card className="border-border/50">
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Quick Actions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button asChild variant="outline" className="w-full justify-start gap-3 h-12">
                  <Link to="/connect-telegram">
                    <FaTelegram className="w-5 h-5 text-primary" />
                    Connect Telegram Account
                    <ExternalLink className="w-4 h-4 ml-auto" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start gap-3 h-12">
                  <Link to="/monitors">
                    <Radio className="w-5 h-5 text-primary" />
                    Create New Monitor
                    <ArrowRight className="w-4 h-4 ml-auto" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-start gap-3 h-12">
                  <Link to="/settings">
                    <Bell className="w-5 h-5 text-primary" />
                    Configure Notifications
                    <ArrowRight className="w-4 h-4 ml-auto" />
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </motion.div>
        </div>

        {/* Recent Alerts */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
        >
          <Card className="border-border/50">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg font-semibold">Recent Alerts</CardTitle>
              <Button asChild variant="ghost" size="sm">
                <Link to="/alerts" className="gap-2">
                  View all
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>
            </CardHeader>
            <CardContent>
              {recentAlerts.length > 0 ? (
                <div className="space-y-4">
                  {recentAlerts.map((alert) => (
                    <div 
                      key={alert.alert_id} 
                      className="flex items-start gap-4 p-4 rounded-lg bg-muted/50 border border-border/50"
                    >
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FaTelegram className="w-5 h-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{alert.channel_title}</span>
                          {alert.matched_keywords?.length > 0 && (
                            <div className="flex gap-1">
                              {alert.matched_keywords.slice(0, 2).map((kw) => (
                                <Badge key={kw} variant="secondary" className="text-xs">
                                  {kw}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground line-clamp-2">
                          {alert.message_text}
                        </p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-muted-foreground">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {new Date(alert.created_at).toLocaleString()}
                          </span>
                          <Badge variant="outline" className="text-xs">
                            {alert.sent_via}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Bell className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
                  <p className="text-muted-foreground">No alerts yet</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Create a monitor to start receiving alerts
                  </p>
                  <Button asChild className="mt-4 rounded-full">
                    <Link to="/monitors">Create Monitor</Link>
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
